import { observer } from "mobx-react";
import React, { FC, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";

import { CloseIcon } from "../../../icons";
import { useAppStore } from "../../../../hooks/useAppStore";
import IpAllowlistBox from "./IpAllowlistBox";
import KeyContentBox from "./KeyContentBox";
import RotatedSecretBox from "./RotatedSecretBox";

interface ManageApiKeyDialogProps {
  apiKeyId: string | null;
  onClose: () => void;
}

const ManageApiKeyDialog: FC<ManageApiKeyDialogProps> = observer(
  ({ apiKeyId, onClose }) => {
    const theme = useTheme();
    const appStore = useAppStore();

    const apiKey = appStore.apiKeys.find((key) => key.id === apiKeyId) ?? null;
    const isOpen = !!apiKeyId && !!apiKey;

    useEffect(() => {
      if (!isOpen) {
        appStore.clearRotatedApiKeySecret();
      }
    }, [isOpen, appStore]);

    const handleClose = () => {
      appStore.clearRotatedApiKeySecret();
      onClose();
    };

    const handleDoneWithSecret = () => {
      appStore.clearRotatedApiKeySecret();
    };

    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: "19px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Manage API key
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& svg": { width: 20, height: 20 },
              }}
            >
              <CloseIcon className="" />
            </Box>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Box sx={{ pt: 2 }}>
            {apiKey ? (
              appStore.rotatedApiKeySecret ? (
                <RotatedSecretBox />
              ) : (
                <Stack spacing={2}>
                  <KeyContentBox apiKey={apiKey} />
                  <IpAllowlistBox isOpen={isOpen} apiKey={apiKey} />
                </Stack>
              )
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {appStore.rotatedApiKeySecret ? (
            <Button
              onClick={handleDoneWithSecret}
              variant="outlined"
              sx={{ ml: "auto" }}
            >
              Done
            </Button>
          ) : (
            <Button onClick={handleClose} variant="outlined" sx={{ ml: "auto" }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

export default ManageApiKeyDialog;
